// Roadmap 2: everything sent to Ledgerate AI about a code workflow goes
// through here. Claude sees the workflow's shape only: source ids and their
// column names, letters, types and format profiles (value kinds and shapes
// such as "99/99/9999", see data/formatProfile.js); each code step's label,
// summary, code, inputs, value fields (the form Claude declared, never what
// the user typed), checks and which of them failed, status, what a failed
// step read last (a column or value name and the kind of value) and result
// columns; and which source columns hold a value hidden in the message.
// Never rows, cell values, the values the user filled in, counts, file names
// or raw error text.
import { CODE_STEP, stepInputs } from "../../../engine";
import { OWN_MESSAGES } from "../../../engine/code/runtime";

const QUOTED = /(["'`])(?:(?!\1)[^\\]|\\.)*\1/g;
const NUMBERS = /\d+(?:\.\d+)?/g;
const MAX_SCANNED_CELLS = 200000;
const IDENTIFIER = /^[A-Za-z_$][\w$]*$/;

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// A result column may be shown when the code names it: as a quoted string,
// or as a whole identifier (row.Total, { Total: … }). Names built from cell
// values are hidden.
const namedInCode = (code, name) =>
  [`"${name}"`, `'${name}'`, `\`${name}\``].some((quoted) => code.includes(quoted)) ||
  (IDENTIFIER.test(name) && new RegExp(`(^|[^\\w$])${escapeRegExp(name)}(?![\\w$])`).test(code));

// True when the text contains a text cell value from these tables (or the
// tables are too big to check, or their rows aren't here), so it must not be sent.
const quotesData = (text, tables) => {
  let scanned = 0;
  for (const table of tables) {
    if (!table.rows) return true;
    for (const row of table.rows) {
      for (const value of Object.values(row)) {
        if (++scanned > MAX_SCANNED_CELLS) return true;
        if (typeof value === "string" && value.length >= 3 && text.includes(value)) return true;
      }
    }
  }
  return false;
};

// The error as Claude may see it: the error kind and the code line, plus the
// built-in message with quoted parts and numbers removed (it can quote cell
// values). Errors the code throws itself never send their message.
export const sanitizeError = (info, inputTables = []) => {
  if (!info) return "Failed.";
  const where = info.line ? ` (line ${info.line})` : "";
  if (info.timeout) return "Timed out: the step ran too long (an endless loop, or too slow for the file).";
  if (info.own && OWN_MESSAGES.includes(info.message)) return `${info.message}${where}`;
  const name = /^\w{1,40}$/.test(info.name || "") ? info.name : "Error";
  if (!info.builtin) return `The code threw ${name === "Error" ? "an error" : `a ${name}`}${where}. Its message stays on the user's device.`;
  const message = String(info.message || "").replace(QUOTED, "…").replace(NUMBERS, "#").slice(0, 200);
  if (quotesData(message, inputTables)) return `${name}${where}. Its message stays on the user's device.`;
  return `${name}: ${message}${where}`;
};

// What a failed step read last (engine/code/runtime.js diagnose), kept only
// when it names one of the step's values or a column its inputs have or its
// code names: a name made from a cell value is never sent.
const safeDetail = (detail, step, tableOf) => {
  if (!detail?.valueType) return null;
  if (detail.value !== undefined) return step.valueFields.some((f) => f.name === detail.value) ? { value: detail.value, valueType: detail.valueType } : null;
  if (!step.inputs.includes(detail.table) || typeof detail.column !== "string") return null;
  const known = (tableOf(detail.table)?.columns || []).some((c) => c.name === detail.column) || namedInCode(step.code, detail.column);
  return known ? { table: detail.table, column: detail.column, valueType: detail.valueType } : null;
};

const columnShape = ({ name, letter, type }) => ({ name, ...(letter ? { letter } : {}), ...(type ? { type } : {}) });

const valueFieldsOf = (step) => step.params?.valueFields || [];

// Sources whose header row the user still has to check: their column names
// come from a row of the file, so they might be data rather than headers.
export const needsHeaderCheck = (source) => Boolean(source.hasHeader) && !source.headerConfirmed;

// The workflow as Claude sees it. `run` must be a run of this exact workflow.
// Built-in steps from Roadmap 1 are left out: Claude only rebuilds code steps.
// `profiles`: { [sourceId]: { [columnName]: profile } } from the data host;
// `placeholders`: where this message's hidden values were found.
export const describeForAi = (workflow, run, { focusStepId, profiles, placeholders } = {}) => {
  if (workflow.sources.some(needsHeaderCheck)) throw new Error("Check the header row of each source first.");
  return describeShape(workflow, run, { focusStepId, profiles, placeholders });
};

const describeShape = (workflow, run, { focusStepId, profiles = {}, placeholders = [] }) => ({
  sources: workflow.sources.map((source) => ({
    id: source.id,
    columns: (run?.tables[source.id]?.columns || source.columns).map((column) => {
      const profile = profiles[source.id]?.[column.name];
      return profile ? { ...columnShape(column), profile } : columnShape(column);
    }),
  })),
  steps: workflow.steps
    .map((step, index) => ({ step, index }))
    .filter(({ step }) => step.type === CODE_STEP)
    .map(({ step, index }) => {
      const inputs = stepInputs(workflow, index);
      const result = run?.results.find((r) => r.stepId === step.id);
      const status = step.disabled ? "disabled" : !result ? "not run (an earlier step failed)" : result.status === "error" ? "failed" : "ok";
      const shape = {
        id: step.id,
        label: step.label || "",
        summary: step.params?.summary || "",
        inputs,
        code: step.params?.code || "",
        codeSig: step.params?.codeSig || "",
        labelSig: step.params?.labelSig || "",
        valueFields: valueFieldsOf(step),
        ...(step.params?.checks?.length ? { checks: step.params.checks } : {}),
        status,
      };
      if (status === "ok" && result?.failedChecks?.length) shape.failedChecks = result.failedChecks;
      const inputTables = inputs.map((id) => run?.tables[id]).filter(Boolean);
      // The data worker checks errors against the input rows (aiText); the UI only has summaries.
      if (status === "failed") {
        shape.error = result.errorInfo?.aiText ?? sanitizeError(result.errorInfo, inputTables);
        const detail = safeDetail(result.errorInfo?.detail, shape, (id) => run?.tables[id]);
        if (detail) shape.errorDetail = detail;
      }
      const table = run?.tables[step.id];
      if (table && (status === "ok" || status === "disabled")) {
        const inputNames = new Set(inputTables.flatMap((t) => t.columns.map((c) => c.name)));
        const visible = table.columns.filter((c) => inputNames.has(c.name) || namedInCode(shape.code, c.name));
        shape.columns = visible.map(({ name, type }) => ({ name, ...(type ? { type } : {}) }));
        if (visible.length < table.columns.length) shape.hiddenColumns = table.columns.length - visible.length;
      }
      return shape;
    }),
  outputs: workflow.outputs.map((output) => ({
    id: output.id,
    format: output.format,
    sheets: (output.sheets || []).map((sheet) => ({ from: sheet.from })),
  })),
  ...(focusStepId ? { focusStepId } : {}),
  ...(placeholders.length ? { placeholders } : {}),
});

// Every value the user still has to fill in: [{ stepId, stepLabel, name, label, value }].
export const valueFields = (workflow) =>
  workflow.steps
    .filter((step) => step.type === CODE_STEP)
    .flatMap((step) => valueFieldsOf(step).map((field) => ({ stepId: step.id, stepLabel: step.label, ...field, value: step.params?.values?.[field.name] })));
